/**
 * PostCard Component
 * 
 * Card component for displaying a blog post preview.
 * Used in the blog listing page.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BlogConfig } from '../BlogConfig';
import { useLanguage } from '../../i18n';

const PostCard = ({ post }) => {
  const { t } = useLanguage();
  const categoryConfig = BlogConfig.categories.find(
    cat => cat.id === post.category
  ) || { label: post.category, color: '#6b7280' };
  
  return (
    <article className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden border border-gray-100">
      {/* Thumbnail */}
      <Link to={`/blog/${post.slug}`} className="block">
        <div className="aspect-video bg-gray-100 overflow-hidden">
          {post.thumbnail ? (
            <img
              src={post.thumbnail}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
              <span className="text-gray-400 text-4xl">📝</span>
            </div>
          )}
        </div>
      </Link>
      
      {/* Content */}
      <div className="p-6">
        {/* Category Badge */}
        <div className="flex items-center gap-2 mb-3">
          <span
            className="px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `${categoryConfig.color}15`,
              color: categoryConfig.color,
            }}
          >
            {t(`blog.categories.${post.category}`) || categoryConfig.label}
          </span>
          
          {post.featured && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              {/* Star emoji or text */}
              ★ {t('blog.featured')}
            </span>
          )}
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
          <Link to={`/blog/${post.slug}`}>
            {post.title}
          </Link>
        </h2>
        
        {/* Description */}
        {post.description && (
          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
            {post.description}
          </p>
        )}
        
        {/* Meta */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <span>{post.formattedDate}</span>
            {BlogConfig.features.enableReadingTime && (
              <>
                <span>·</span>
                <span>{post.readingTime} {t('blog.minuteRead')}</span>
              </>
            )}
          </div>
          
          {post.tags.length > 0 && BlogConfig.features.enableTags && (
            <div className="flex gap-1">
              {post.tags.slice(0, 2).map(tag => (
                <span
                  key={tag}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default PostCard;
